/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useMemo, useState } from 'react';
import {
  Button as ButtonAnt,
  Col,
  Form,
  FormProps,
  Input,
  Row,
  Select,
  Typography
} from 'antd';
import { useQuery } from '@/hooks/query.hook';
import { PageURLs } from '@/utils/navigate';
import { Icon } from '../icons';
import { Button } from '../button';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getListRoles, watchRoleList } from '@/redux/slices/roles.slice';
import {
  createAdminUser,
  resetMessages,
  watchAdminUserLoading,
  watchAdminUserMessage
} from '@/redux/slices/admin.slice';
import { setMessages } from '@/redux/slices/app.slice';
import { Message } from '@/enums/message.enum';
import { validateEmail } from '@/utils/validator';
import { useBeforeUnload } from 'react-router-dom';

export const AdminCreateForm = () => {
  const { push } = useQuery();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const rolesList = useAppSelector(watchRoleList);
  const messageInfo = useAppSelector(watchAdminUserMessage);
  const loading = useAppSelector(watchAdminUserLoading);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const rolesOptions = useMemo(
    () =>
      rolesList.map((role) => ({
        value: role.id,
        label: role.name
      })),
    [rolesList]
  );

  const handleGoBack = () => {
    push(PageURLs.ofAdminManagement());
  };

  const onFinish: FormProps<CreateAdminUserProps>['onFinish'] = async (
    values
  ) => {
    const result = await dispatch(createAdminUser(values));

    if (createAdminUser.fulfilled.match(result)) {
      handleGoBack();
    }
  };

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      if (messageInfo.type === Message.SUCCESS) {
        form.resetFields();
      }
      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo, form]);

  useEffect(() => {
    dispatch(getListRoles());
  }, [dispatch]);

  useBeforeUnload((e) => {
    e.preventDefault();
  });

  return (
    <div css={rootStyles}>
      <Typography.Title css={titleStyles} level={5}>
        <ButtonAnt
          css={goBackBtnStyles}
          onClick={handleGoBack}
          type='text'
          icon={<Icon type='arrowLeft' width={20} height={20} />}
        />
        Thêm mới quản trị viên
      </Typography.Title>
      <Form
        form={form}
        name='create-admin'
        css={formStyles}
        initialValues={{ remember: true }}
        onFinish={onFinish}
        autoComplete='off'
        className='ant-form-custom'
      >
        <Form.Item<CreateAdminUserProps>
          label='Email (*)'
          name='email'
          rules={[
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject('Email là bắt buộc');
                } else if (!validateEmail(value)) {
                  return Promise.reject('Định dạng email chưa đúng');
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input css={inputStyles} size='large' placeholder='Nhập email' />
        </Form.Item>

        <Form.Item<CreateAdminUserProps>
          label='Vai trò (*)'
          name='roleIds'
          rules={[
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject('Vai trò là bắt buộc');
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Select
            showSearch
            css={inputStyles}
            onChange={setSelectedItems}
            value={selectedItems}
            options={rolesOptions}
            placeholder='Chọn vai trò'
            size='large'
            mode='multiple'
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Row css={formBottomStyles} justify='center' gutter={[20, 0]}>
          <Col>
            <Button onClick={handleGoBack} css={actionBtnStyles} shape='round'>
              Huỷ bỏ
            </Button>
          </Col>
          <Col>
            <Button
              loading={loading}
              css={actionBtnStyles}
              htmlType='submit'
              type='primary'
              shape='round'
            >
              Lưu
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem;
`;

const formStyles = css`
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  .ant-form-item {
    .ant-form-item-label {
      width: 12rem;
      text-align: left;
      font-weight: 500;
      line-height: 5rem;
    }
  }
`;

const inputStyles = css`
  height: 5rem;
`;

const titleStyles = css`
  display: flex;
  gap: 0.6rem;
  align-items: center;
  position: relative;
  left: 2rem;
  margin-bottom: 0 !important;
`;

const goBackBtnStyles = css`
  position: absolute;
  left: -3rem;
  width: 2.6rem !important;
  height: 2.6rem !important;
  padding: 0;
  svg {
    margin-top: 0.1rem;
  }
  &:hover {
    border-radius: 50%;
  }
`;

const actionBtnStyles = css`
  width: 10rem;
  font-weight: 500;
`;

const formBottomStyles = css`
  margin-top: 2rem;
`;
